import React__default, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '../../../node_modules/@fortawesome/react-fontawesome/index.es.js';
import { faChevronLeft, faChevronRight, faCheck } from '../../../node_modules/@fortawesome/free-solid-svg-icons/index.js';
import styles from './styles.module.scss.js';
import { NvButton } from '../NvButton/component.js';
import { NvProgressBar } from '../NvProgressBar/component.js';
import propsToClass from '../../../helpers/propsToClass.js';

const emptyFunction = () => { };
/**
 * @description Nimvelo MultiStage component
 * @props className, completeButtonText, controlPosition, fullWidth, onComplete,
 * showStageNumbers, showStageProgressBar, stages, defaultState
 */
const NvMultiStage = ({ disabled, className, completeButtonText = 'Done', controlPosition = 'bottom', defaultState = {}, fullWidth = false, onComplete, showStageNumbers = false, showStageProgressBar = false, stages, }) => {
    /**
     * Type guard functions for stages prop
     *
     * Returns stages as ControlledStage
     */
    const isStageControlled = (
    // eslint-disable-next-line no-shadow
    stages) => {
        const s = stages[0];
        return !!s.identifier || !!s.next || !!s.back;
    };
    /**
     * Type guard function for determining key is in obj
     *
     * If returns true, allows that key to index obj in code to follow
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function hasKey(obj, key) {
        return key in obj;
    }
    const [currentStage, setCurrentStage] = useState(isStageControlled(stages) ? stages[0].identifier : 1);
    // * create state with user defined state
    const [userState, setUserState] = useState(defaultState);
    const userStateRef = useRef(defaultState);
    // * state controller exposed to user
    const stateController = {};
    // * for each key in user state, we create accessors to get and set value
    Object.keys(defaultState).forEach(key => {
        if (!hasKey(defaultState, key))
            return;
        const value = userState[key];
        Object.defineProperty(stateController, key, {
            set(newState) {
                userStateRef.current = { ...userStateRef.current, [key]: newState };
                setUserState(userStateRef.current);
            },
            get() {
                return value;
            },
        });
    });
    /**
     * Returns the current controlled stage based on the identifier
     * stored in currentStage
     */
    const getControlledStage = () => {
        const controlledStage = stages.find(stage => stage.identifier === currentStage);
        if (!controlledStage)
            // eslint-disable-next-line no-console
            console.warn('Could not find stage with identifier:', currentStage);
        return controlledStage;
    };
    /**
     * Returns the current stage based on the index stored in currentStage
     * i.e. stages[currentStage]
     */
    const getOrderedStage = () => stages[currentStage - 1];
    /**
     * Controlled stage - set next stage to be [currentControlledStage].next
     *
     * Ordered stage - set next stage to be stages[currentStage + 1]
     */
    const handleNext = () => {
        if (isStageControlled(stages)) {
            const thisStage = getControlledStage();
            if (thisStage?.next) {
                const goto = thisStage.next(userStateRef.current);
                setCurrentStage(goto || currentStage);
            }
        }
        else {
            setCurrentStage(currentStage + 1);
        }
    };
    /**
     * Controlled stage - set next stage to be [currentControlledStage].back
     *
     * Ordered stage - set next stage to be stages[currentStage - 1]
     */
    const handleBack = () => {
        if (isStageControlled(stages)) {
            const thisStage = getControlledStage();
            if (thisStage?.back) {
                const goto = thisStage.back(userStateRef.current);
                setCurrentStage(goto || currentStage);
            }
        }
        else {
            setCurrentStage(currentStage - 1);
        }
    };
    /**
     * Determines if next button should be disabled
     */
    const isDisabledButton = () => {
        const allowNextClick = isStageControlled(stages)
            ? getControlledStage()?.allowNextClick
            : getOrderedStage().allowNextClick;
        return allowNextClick ? !allowNextClick(userStateRef.current) : false;
    };
    const hasNextStage = isStageControlled(stages)
        ? getControlledStage()?.next?.(stateController) !== undefined
        : currentStage !== stages.length;
    return (React__default.createElement("div", { className: propsToClass(`${styles.container} ${className} ${styles[`control_${controlPosition}`]}`, [[styles.full_width, fullWidth]]) },
        React__default.createElement("div", { className: styles.stage_wrapper },
            React__default.createElement("div", { className: styles.stage }, isStageControlled(stages)
                ? getControlledStage()?.render(stateController, hasNextStage ? handleNext : emptyFunction)
                : getOrderedStage().render(stateController, hasNextStage ? handleNext : emptyFunction))),
        React__default.createElement("div", { className: styles.controls },
            React__default.createElement(NvButton, { onClick: handleBack, kind: "danger", leftIcon: React__default.createElement(FontAwesomeIcon, { icon: faChevronLeft }), disabled: disabled ||
                    (isStageControlled(stages)
                        ? getControlledStage()?.back?.(userStateRef.current) === undefined
                        : currentStage === 1), inverted: true, borderless: true, size: "standard", className: styles.buttons }, "Back"),
            showStageNumbers && !isStageControlled(stages) && (React__default.createElement("span", { className: styles.stage_numbers }, `${currentStage} / ${stages.length}`)),
            hasNextStage &&
                ((isStageControlled(stages)
                    ? !getControlledStage()?.hideNextButton
                    : !getOrderedStage()?.hideNextButton) ? (React__default.createElement(NvButton, { onClick: handleNext, rightIcon: React__default.createElement(FontAwesomeIcon, { icon: faChevronRight }), disabled: disabled || isDisabledButton(), inverted: true, borderless: true, size: "standard", className: styles.buttons }, "Next")) : (React__default.createElement("div", { className: `${styles.buttons} ${styles.emptyButton}` }))),
            (isStageControlled(stages)
                ? getControlledStage()?.next?.(userStateRef.current) === undefined
                : currentStage === stages.length) && (React__default.createElement(NvButton, { disabled: disabled || isDisabledButton(), kind: "success", onClick: () => {
                    onComplete(userStateRef.current);
                }, rightIcon: React__default.createElement(FontAwesomeIcon, { icon: faCheck }), inverted: true, borderless: true, size: "standard", className: styles.buttons }, completeButtonText)),
            showStageProgressBar && !isStageControlled(stages) && (React__default.createElement("div", { className: styles.stage_progress_bar },
                React__default.createElement(NvProgressBar, { progressPercentage: (100 * currentStage) / stages.length, fullWidth: true, size: "xsmall", rounded: true }))))));
};

export { NvMultiStage, NvMultiStage as default };
//# sourceMappingURL=component.js.map
